import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function NewsletterUnsubscribe() {
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const unsubscribe = async () => {
      const params = new URLSearchParams(location.search);
      const email = params.get('email');

      if (!email) {
        setStatus('error');
        setMessage('Invalid unsubscribe link');
        return;
      }

      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/blog/newsletter/unsubscribe?email=${encodeURIComponent(email)}`
        );

        if (response.data.success) {
          setStatus('success');
          setMessage(response.data.message);
        }
      } catch (error) {
        setStatus('error');
        setMessage(
          error.response?.data?.detail || 
          'Unsubscribe failed. Please try again later.'
        );
      }
    };

    unsubscribe();
  }, [location.search]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto mb-4 text-red-600 animate-spin" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Processing...</h1>
            <p className="text-gray-600 dark:text-gray-400">Please wait while we process your request.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-600" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Unsubscribed Successfully</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-4">{message}</p>
            <p className="text-sm text-gray-500 mb-6">
              We're sorry to see you go. You won't receive any more newsletters from us.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/blog')}
                className="w-full bg-red-600 hover:bg-red-700"
              >
                Visit Our Blog
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full"
              >
                Go to Homepage
              </Button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 mx-auto mb-4 text-red-600" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Unsubscribe Failed</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
            <Button
              onClick={() => navigate('/blog')}
              className="w-full bg-red-600 hover:bg-red-700"
            >
              Return to Blog
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
